import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useProductionModalAudit } from '../../../../audit/useProductionModalAudit';

interface RenameJobNumberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobNumbers: string[];
  onConfirm: (oldJobNumber: string, newJobNumber: string) => void;
  loading?: boolean;
  projectId?: number | string | null;
  projectName?: string | null;
}

export const RenameJobNumberDialog: React.FC<RenameJobNumberDialogProps> = ({
  open,
  onOpenChange,
  jobNumbers,
  onConfirm,
  loading = false,
  projectId,
  projectName,
}) => {
  useProductionModalAudit(open, 'Rename Job Number', projectId, projectName);
  const [selectedJobNumber, setSelectedJobNumber] = useState('');
  const [newJobNumber, setNewJobNumber] = useState('');
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedJobNumber(jobNumbers[0] ?? '');
      setNewJobNumber('');
      setShowWarning(false);
    }
  }, [open, jobNumbers]);

  const isConfirmDisabled =
    !selectedJobNumber.trim() ||
    !newJobNumber.trim() ||
    selectedJobNumber.trim().toLowerCase() === newJobNumber.trim().toLowerCase() ||
    loading;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!loading) {
      onOpenChange(nextOpen);
    }
  };

  const handleConfirm = () => {
    if (!isConfirmDisabled) {
      setShowWarning(true);
    }
  };

  const handleYes = () => {
    if (!loading) {
      onConfirm(selectedJobNumber.trim(), newJobNumber.trim());
    }
  };

  const handleNo = () => {
    if (!loading) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-white max-w-3xl w-full">
        <DialogHeader className="pl-0 pr-14 pb-4">
          <div className="flex items-center justify-between gap-4">
            <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
              Rename Job Number
            </DialogTitle>
            {!showWarning && (
              <Button
                onClick={handleConfirm}
                disabled={isConfirmDisabled}
                className="bg-hero-red hover:bg-hero-red-60 text-white"
              >
                Confirm
              </Button>
            )}
          </div>
        </DialogHeader>

        {showWarning ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
              <div>
                <p className="font-medium">Before you rename</p>
                <p className="mt-0.5">
                  If you download weld parameters again, the file may still use the old job number and will not
                  reflect this change. Are you sure you want to rename{' '}
                  <strong>{selectedJobNumber}</strong> to <strong>{newJobNumber}</strong>?
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={handleNo}
                disabled={loading}
                className="border-[#E2E5EA] text-text-grey"
              >
                No
              </Button>
              <Button
                onClick={handleYes}
                disabled={loading}
                className="bg-hero-red hover:bg-hero-red-60 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Renaming...
                  </>
                ) : (
                  'Yes'
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-[13px] text-text-grey">
              Select the current job number for this pipe and enter the new job number.
              This updates the job number across weld status, limits, parameters for this project.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="currentJobNumber" className="text-[12px] font-medium text-text-grey">
                  Current job number
                </Label>
                {jobNumbers.length > 0 ? (
                  <Select
                    value={selectedJobNumber}
                    onValueChange={setSelectedJobNumber}
                    disabled={loading}
                  >
                    <SelectTrigger
                      id="currentJobNumber"
                      className="bg-white border-[#E2E5EA] text-text-grey"
                    >
                      <SelectValue placeholder="Select job number" />
                    </SelectTrigger>
                    <SelectContent>
                      {jobNumbers.map((jobNumber) => (
                        <SelectItem key={jobNumber} value={jobNumber}>
                          {jobNumber}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-[13px] text-text-grey">No job numbers found for the selected pipe.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="newJobNumber" className="text-[12px] font-medium text-text-grey">
                  New job number
                </Label>
                <Input
                  id="newJobNumber"
                  value={newJobNumber}
                  onChange={(e) => setNewJobNumber(e.target.value)}
                  placeholder="Enter new job number"
                  className="bg-white border-[#E2E5EA] text-text-grey placeholder:text-heritage-grey"
                  disabled={loading || jobNumbers.length === 0}
                  autoFocus
                />
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
